// ============================================================
// Attendance Report Page — 出勤紀錄月報
// 月份選擇 + 員工出勤摘要表格 + 展開每日明細
// ============================================================

import React, { useEffect, useState, useCallback } from 'react';
import { api } from '../lib/api';
import { exportCSV } from '../lib/csvExport';
import { formatTaipeiTime, shiftMonth, taipeiThisMonth } from '../lib/datetime';

interface DailyRecord {
  id?: string;          // attendance.id — 沒打卡則無
  date: string;
  scheduled: boolean;
  scheduleStart?: string;
  scheduleEnd?: string;
  scheduleKind?: string;   // WORK | REGULAR_LEAVE | REST_DAY
  scheduleName?: string;
  clockIn?: string;
  clockOut?: string;
  status: string;
  workHours: number;
  overtimeHours: number;
  // 加班核准（NOT_APPLICABLE | PENDING | APPROVED | REJECTED）
  overtimeMinutes?: number;
  overtimeApprovalStatus?: string;
}

interface StaffReport {
  userId: string;
  userName: string;
  userAvatar: string | null;
  role: string;
  scheduledDays: number;
  presentDays: number;
  normalCount: number;
  lateCount: number;
  earlyLeaveCount: number;
  absentDays: number;
  totalWorkHours: number;
  regularHours: number;
  overtimeHours: number;
  dailyRecords: DailyRecord[];
}

interface ReportData {
  month: string;
  daysInMonth: number;
  staffReports: StaffReport[];
  alerts: { userId: string; userName: string; type: string; message: string }[];
  summary: {
    totalStaff: number;
    totalScheduledDays: number;
    totalPresentDays: number;
    totalAbsentDays: number;
    totalWorkHours: number;
    totalOvertimeHours: number;
  };
}

const STATUS_LABEL: Record<string, { text: string; color: string }> = {
  NORMAL: { text: '正常', color: 'text-sage-600' },
  LATE: { text: '遲到', color: 'text-rose-500' },
  EARLY_LEAVE: { text: '早退', color: 'text-amber-600' },
  ABSENT: { text: '缺勤', color: 'text-red-500' },
  MISSED: { text: '漏卡', color: 'text-orange-500' },
  PENDING: { text: '待審', color: 'text-blue-500' },
  REGULAR_LEAVE: { text: '例假日', color: 'text-morandi-400' },
  REST_DAY: { text: '休息日', color: 'text-morandi-400' },
};

function formatDateShort(dateStr: string): string {
  const d = new Date(dateStr);
  return `${d.getMonth() + 1}/${d.getDate()}`;
}

const WEEKDAY = ['日', '一', '二', '三', '四', '五', '六'];

export function AttendanceReportPage() {
  const [month, setMonth] = useState(taipeiThisMonth);
  const [data, setData] = useState<ReportData | null>(null);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [editingRec, setEditingRec] = useState<{ rec: DailyRecord; userName: string } | null>(null);
  const [manualEntry, setManualEntry] = useState<{ userId: string; userName: string; date: string; scheduleStart?: string; scheduleEnd?: string } | null>(null);

  const handleExportCSV = () => {
    if (!data) return;
    // 摘要表
    const summaryHeaders = [
      '員工', '排班天數', '出勤天數', '正常', '遲到', '早退', '缺勤',
      '總工時', '正常工時', '加班時數',
    ];
    const summaryRows = data.staffReports.map(s => [
      s.userName,
      s.scheduledDays,
      s.presentDays,
      s.normalCount,
      s.lateCount,
      s.earlyLeaveCount,
      s.absentDays,
      s.totalWorkHours,
      s.regularHours,
      s.overtimeHours,
    ]);
    exportCSV(`出勤月報_${month}_摘要.csv`, summaryHeaders, summaryRows);

    // 每日明細表
    const detailHeaders = [
      '員工', '日期', '排班時段', '上班打卡', '下班打卡',
      '狀態', '工時', '加班時數',
    ];
    const detailRows: (string | number | null | undefined)[][] = [];
    for (const s of data.staffReports) {
      for (const rec of s.dailyRecords) {
        const clockIn = rec.clockIn ? new Date(rec.clockIn).toLocaleString('zh-TW', { hour12: false }) : '';
        const clockOut = rec.clockOut ? new Date(rec.clockOut).toLocaleString('zh-TW', { hour12: false }) : '';
        const schedSlot = rec.scheduleStart && rec.scheduleEnd ? `${rec.scheduleStart}-${rec.scheduleEnd}` : '';
        detailRows.push([
          s.userName,
          rec.date,
          schedSlot,
          clockIn,
          clockOut,
          rec.status,
          rec.workHours,
          rec.overtimeHours,
        ]);
      }
    }
    exportCSV(`出勤月報_${month}_明細.csv`, detailHeaders, detailRows);
  };

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get<ReportData>(`/attendance-report?month=${month}`);
      setData(res);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, [month]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const navigateMonth = (delta: number) => setMonth(m => shiftMonth(m, delta));

  const [y, m] = month.split('-').map(Number);
  const monthLabel = `${y} 年 ${m} 月`;

  return (
    <div className="max-w-6xl mx-auto px-4 py-6 space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-morandi-800">出勤紀錄月報</h1>
      </div>

      {/* 月份選擇 + 匯出 */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigateMonth(-1)}
            className="w-9 h-9 rounded-full flex items-center justify-center text-morandi-500 hover:bg-morandi-100 transition-colors"
          >
            ‹
          </button>
          <span className="text-lg font-semibold text-morandi-700 min-w-[120px] text-center">{monthLabel}</span>
          <button
            onClick={() => navigateMonth(1)}
            className="w-9 h-9 rounded-full flex items-center justify-center text-morandi-500 hover:bg-morandi-100 transition-colors"
          >
            ›
          </button>
        </div>
        {data && (
          <button
            onClick={handleExportCSV}
            className="px-4 py-2 text-sm bg-earth-400 hover:bg-earth-500 text-white rounded-btn transition-colors"
          >
            ↓ 匯出 CSV（摘要+明細）
          </button>
        )}
      </div>

      {/* 月摘要 */}
      {data && (
        <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
          <MiniStat label="員工數" value={data.summary.totalStaff} unit="人" />
          <MiniStat label="排班" value={data.summary.totalScheduledDays} unit="班" />
          <MiniStat label="出勤" value={data.summary.totalPresentDays} unit="天" />
          <MiniStat label="缺勤" value={data.summary.totalAbsentDays} unit="天" highlight={data.summary.totalAbsentDays > 0} />
          <MiniStat label="總工時" value={data.summary.totalWorkHours} unit="hr" />
          <MiniStat label="加班" value={data.summary.totalOvertimeHours} unit="hr" highlight={data.summary.totalOvertimeHours > 0} />
        </div>
      )}

      {/* 加班警告 */}
      {data && data.alerts.length > 0 && (
        <div className="space-y-2">
          {data.alerts.map((a, i) => (
            <div key={i} className="flex items-center gap-2 px-3 py-2 bg-rose-50 border border-rose-200 rounded-lg text-sm text-rose-600">
              <span>!</span>
              <span>{a.message}</span>
            </div>
          ))}
        </div>
      )}

      {/* 員工列表 */}
      {loading ? (
        <div className="text-center py-10 text-morandi-400">載入中...</div>
      ) : data && data.staffReports.length > 0 ? (
        <div className="card-morandi overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-morandi-50 text-morandi-500 text-xs">
                <th className="text-left px-4 py-3 font-medium">員工</th>
                <th className="text-center px-2 py-3 font-medium">排班</th>
                <th className="text-center px-2 py-3 font-medium">出勤</th>
                <th className="text-center px-2 py-3 font-medium">正常</th>
                <th className="text-center px-2 py-3 font-medium">遲到</th>
                <th className="text-center px-2 py-3 font-medium">早退</th>
                <th className="text-center px-2 py-3 font-medium">缺勤</th>
                <th className="text-center px-2 py-3 font-medium">工時</th>
                <th className="text-center px-2 py-3 font-medium">加班</th>
              </tr>
            </thead>
            <tbody>
              {data.staffReports.map((staff) => {
                const isExpanded = expandedId === staff.userId;
                return (
                  <React.Fragment key={staff.userId}>
                    <tr
                      className="border-t border-morandi-100 hover:bg-morandi-50 cursor-pointer transition-colors"
                      onClick={() => setExpandedId(isExpanded ? null : staff.userId)}
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-earth-100 flex items-center justify-center text-earth-600 text-xs font-medium shrink-0">
                            {staff.userName.slice(0, 1)}
                          </div>
                          <div>
                            <div className="font-medium text-morandi-700">{staff.userName}</div>
                          </div>
                          <span className="text-morandi-300 text-xs ml-1">{isExpanded ? '▲' : '▼'}</span>
                        </div>
                      </td>
                      <td className="text-center px-2 py-3 font-mono">{staff.scheduledDays}</td>
                      <td className="text-center px-2 py-3 font-mono">{staff.presentDays}</td>
                      <td className="text-center px-2 py-3 font-mono text-sage-600">{staff.normalCount}</td>
                      <td className="text-center px-2 py-3 font-mono text-rose-500">{staff.lateCount || '-'}</td>
                      <td className="text-center px-2 py-3 font-mono text-amber-600">{staff.earlyLeaveCount || '-'}</td>
                      <td className="text-center px-2 py-3 font-mono text-red-500">{staff.absentDays || '-'}</td>
                      <td className="text-center px-2 py-3 font-mono">{staff.totalWorkHours}</td>
                      <td className={`text-center px-2 py-3 font-mono ${staff.overtimeHours > 0 ? 'text-rose-500' : ''}`}>
                        {staff.overtimeHours || '-'}
                      </td>
                    </tr>

                    {/* 展開的每日明細 */}
                    {isExpanded && staff.dailyRecords.length > 0 && (
                      <tr>
                        <td colSpan={9} className="bg-morandi-50 px-4 py-3">
                          <div className="max-h-[300px] overflow-y-auto scrollbar-hide">
                            <table className="w-full text-xs">
                              <thead>
                                <tr className="text-morandi-400">
                                  <th className="text-left py-1.5 px-2 font-medium">日期</th>
                                  <th className="text-center py-1.5 px-2 font-medium">排班時間</th>
                                  <th className="text-center py-1.5 px-2 font-medium">上班打卡</th>
                                  <th className="text-center py-1.5 px-2 font-medium">下班打卡</th>
                                  <th className="text-center py-1.5 px-2 font-medium">狀態</th>
                                  <th className="text-center py-1.5 px-2 font-medium">工時</th>
                                  <th className="text-center py-1.5 px-2 font-medium">加班</th>
                                  <th className="text-center py-1.5 px-2 font-medium">操作</th>
                                </tr>
                              </thead>
                              <tbody>
                                {staff.dailyRecords.map((rec) => {
                                  const d = new Date(rec.date);
                                  const weekday = WEEKDAY[d.getDay()];
                                  const isWeekend = d.getDay() === 0 || d.getDay() === 6;
                                  const statusCfg = STATUS_LABEL[rec.status] ?? { text: rec.status, color: 'text-morandi-500' };
                                  return (
                                    <tr key={rec.date} className={`border-t border-morandi-100 ${isWeekend ? 'bg-morandi-100/50' : ''}`}>
                                      <td className="py-1.5 px-2 text-morandi-600">
                                        {formatDateShort(rec.date)}
                                        <span className={`ml-1 ${isWeekend ? 'text-rose-400' : 'text-morandi-300'}`}>({weekday})</span>
                                      </td>
                                      <td className="text-center py-1.5 px-2 font-mono text-morandi-500">
                                        {(() => {
                                          // 例假日/休息日：顯示班別名稱 + icon，不顯示 00:00–00:00
                                          if (rec.scheduleKind === 'REGULAR_LEAVE') return <span className="text-morandi-400">📌 {rec.scheduleName ?? '例假日'}</span>;
                                          if (rec.scheduleKind === 'REST_DAY') return <span className="text-morandi-400">💤 {rec.scheduleName ?? '休息日'}</span>;
                                          if (rec.scheduleStart && rec.scheduleEnd) return `${rec.scheduleStart}–${rec.scheduleEnd}`;
                                          return '-';
                                        })()}
                                      </td>
                                      <td className="text-center py-1.5 px-2 font-mono text-morandi-600">{formatTaipeiTime(rec.clockIn)}</td>
                                      <td className="text-center py-1.5 px-2 font-mono text-morandi-600">{formatTaipeiTime(rec.clockOut)}</td>
                                      <td className={`text-center py-1.5 px-2 font-medium ${statusCfg.color}`}>{statusCfg.text}</td>
                                      <td className="text-center py-1.5 px-2 font-mono">{rec.workHours || '-'}</td>
                                      <td className={`text-center py-1.5 px-2 font-mono ${rec.overtimeHours > 0 ? 'text-rose-500' : ''}`}>
                                        {(() => {
                                          const otStatus = rec.overtimeApprovalStatus ?? 'NOT_APPLICABLE';
                                          const otMin = rec.overtimeMinutes ?? 0;
                                          if (otStatus === 'PENDING' && otMin > 0) {
                                            return <span className="text-amber-600">⏳ {otMin}m</span>;
                                          }
                                          if (otStatus === 'REJECTED' && otMin > 0) {
                                            return <span className="text-rose-400 line-through">{otMin}m</span>;
                                          }
                                          if (otStatus === 'APPROVED' && otMin > 0) {
                                            return <span className="text-sage-600">✓ {otMin}m</span>;
                                          }
                                          return rec.overtimeHours || '-';
                                        })()}
                                      </td>
                                      <td className="text-center py-1.5 px-2">
                                        {rec.id ? (
                                          <button
                                            onClick={() => setEditingRec({ rec, userName: staff.userName })}
                                            className="text-xs text-earth-500 hover:text-earth-700 px-1"
                                          >編輯</button>
                                        ) : (rec.scheduleKind === 'REGULAR_LEAVE' || rec.scheduleKind === 'REST_DAY') ? (
                                          <span className="text-xs text-morandi-300">-</span>
                                        ) : (
                                          <button
                                            onClick={() => setManualEntry({ userId: staff.userId, userName: staff.userName, date: rec.date, scheduleStart: rec.scheduleStart, scheduleEnd: rec.scheduleEnd })}
                                            className="text-xs text-sage-600 hover:text-sage-800 px-1"
                                          >📝 補登</button>
                                        )}
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        </td>
                      </tr>
                    )}

                    {isExpanded && staff.dailyRecords.length === 0 && (
                      <tr>
                        <td colSpan={9} className="bg-morandi-50 px-4 py-4 text-center text-morandi-300 text-xs">
                          本月無出勤紀錄
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="text-center py-10 text-morandi-300">本月無資料</div>
      )}

      {editingRec && (
        <EditAttendanceModal
          rec={editingRec.rec}
          userName={editingRec.userName}
          onClose={() => setEditingRec(null)}
          onSaved={() => { setEditingRec(null); fetchReport(); }}
        />
      )}

      {manualEntry && (
        <ManualEntryModal
          userId={manualEntry.userId}
          userName={manualEntry.userName}
          date={manualEntry.date}
          defaultStart={manualEntry.scheduleStart}
          defaultEnd={manualEntry.scheduleEnd}
          onClose={() => setManualEntry(null)}
          onSaved={() => { setManualEntry(null); fetchReport(); }}
        />
      )}
    </div>
  );
}

// 老闆/主管補登整筆打卡（員工漏打卡時用，跳過簽核流程）
function ManualEntryModal({ userId, userName, date, defaultStart, defaultEnd, onClose, onSaved }: {
  userId: string;
  userName: string;
  date: string;         // YYYY-MM-DD Taipei
  defaultStart?: string; // 'HH:MM' 排班預設
  defaultEnd?: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [startTime, setStartTime] = useState(defaultStart ?? '');
  const [endTime, setEndTime] = useState(defaultEnd ?? '');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  // 把 Taipei date + HH:MM 轉成 ISO（強制 +08:00）
  const toIsoTaipei = (hhmm: string): string | null => {
    if (!/^\d{2}:\d{2}$/.test(hhmm)) return null;
    return new Date(`${date}T${hhmm}:00+08:00`).toISOString();
  };

  const handleSave = async () => {
    if (!startTime) { alert('請填上班時間'); return; }
    const clockInIso = toIsoTaipei(startTime);
    if (!clockInIso) { alert('上班時間格式錯誤'); return; }
    const clockOutIso = endTime ? toIsoTaipei(endTime) : null;
    if (endTime && !clockOutIso) { alert('下班時間格式錯誤'); return; }
    if (!reason.trim()) { alert('請填補登原因（例如：員工漏打卡）'); return; }

    setSaving(true);
    try {
      await api.post('/attendance/manual-create', {
        userId,
        date,
        clockIn: clockInIso,
        clockOut: clockOutIso,
        reason: reason.trim(),
      });
      onSaved();
    } catch (err) {
      alert('補登失敗：' + (err as Error).message);
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-5 space-y-4 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div>
          <h2 className="text-lg font-bold text-morandi-800">📝 補登打卡</h2>
          <div className="text-sm text-morandi-500 mt-0.5">{userName} · {date}</div>
        </div>

        <div className="bg-sage-50 border border-sage-200 rounded-lg p-3 text-xs text-sage-700">
          補登後狀態為「正常 / 已核准」，不需員工簽核確認。
          {defaultStart && defaultEnd && (
            <div className="mt-1 text-sage-600">排班時段：{defaultStart}–{defaultEnd}（已預填）</div>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-sm text-morandi-500 block mb-1">上班時間 *</label>
            <input
              type="time"
              value={startTime}
              onChange={e => setStartTime(e.target.value)}
              className="input-field"
            />
          </div>
          <div>
            <label className="text-sm text-morandi-500 block mb-1">下班時間</label>
            <input
              type="time"
              value={endTime}
              onChange={e => setEndTime(e.target.value)}
              placeholder="可空白（之後再補）"
              className="input-field"
            />
          </div>
        </div>

        <div>
          <label className="text-sm text-morandi-500 block mb-1">補登原因 *</label>
          <input
            type="text"
            value={reason}
            onChange={e => setReason(e.target.value)}
            placeholder="例：員工漏打卡 / 系統異常"
            maxLength={500}
            className="input-field"
          />
        </div>

        <div className="flex gap-2 pt-2">
          <button
            onClick={onClose}
            disabled={saving}
            className="flex-1 py-2.5 bg-morandi-100 hover:bg-morandi-200 text-morandi-600 rounded-btn text-sm disabled:opacity-50"
          >取消</button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex-1 py-2.5 bg-sage-500 hover:bg-sage-600 text-white rounded-btn text-sm disabled:opacity-50"
          >{saving ? '補登中…' : '補登'}</button>
        </div>
      </div>
    </div>
  );
}

// 老闆/主管編輯打卡時間
function EditAttendanceModal({ rec, userName, onClose, onSaved }: {
  rec: DailyRecord;
  userName: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const toLocalInput = (iso?: string) => {
    if (!iso) return '';
    const d = new Date(iso);
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  const [clockIn, setClockIn] = useState(toLocalInput(rec.clockIn));
  const [clockOut, setClockOut] = useState(toLocalInput(rec.clockOut));
  const [status, setStatus] = useState(rec.status);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!rec.id) return;
    setSaving(true);
    try {
      await api.put(`/attendance/${rec.id}`, {
        clockIn: clockIn ? new Date(clockIn).toISOString() : null,
        clockOut: clockOut ? new Date(clockOut).toISOString() : null,
        status,
      });
      onSaved();
    } catch (err) {
      alert('儲存失敗：' + (err as Error).message);
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!rec.id) return;
    if (!window.confirm(`確定要刪除 ${userName} 在 ${rec.date} 的這筆打卡紀錄嗎？\n\n此動作無法復原。`)) return;
    setSaving(true);
    try {
      await api.delete(`/attendance/${rec.id}`);
      onSaved();
    } catch (err) {
      alert('刪除失敗：' + (err as Error).message);
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-5 space-y-4 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <h2 className="text-lg font-bold text-morandi-800">
          編輯打卡紀錄
          <span className="ml-2 text-sm text-morandi-400 font-normal">{userName} · {rec.date}</span>
        </h2>

        <div>
          <label className="text-sm text-morandi-500 block mb-1">上班打卡時間</label>
          <input
            type="datetime-local"
            value={clockIn}
            onChange={e => setClockIn(e.target.value)}
            className="input-field"
          />
          <button
            onClick={() => setClockIn('')}
            className="text-xs text-morandi-400 hover:text-rose-500 mt-1"
          >清除</button>
        </div>

        <div>
          <label className="text-sm text-morandi-500 block mb-1">下班打卡時間</label>
          <input
            type="datetime-local"
            value={clockOut}
            onChange={e => setClockOut(e.target.value)}
            className="input-field"
          />
          <button
            onClick={() => setClockOut('')}
            className="text-xs text-morandi-400 hover:text-rose-500 mt-1"
          >清除</button>
        </div>

        <div>
          <label className="text-sm text-morandi-500 block mb-1">狀態</label>
          <select
            value={status}
            onChange={e => setStatus(e.target.value)}
            className="input-field"
          >
            <option value="NORMAL">正常</option>
            <option value="LATE">遲到</option>
            <option value="EARLY_LEAVE">早退</option>
            <option value="MISSED">漏卡</option>
            <option value="PENDING">待審</option>
          </select>
        </div>

        {/* 加班核准區塊（僅當 PENDING 時顯示）*/}
        {rec.overtimeApprovalStatus === 'PENDING' && (rec.overtimeMinutes ?? 0) > 0 && (
          <OvertimeApprovalSection
            recId={rec.id!}
            minutes={rec.overtimeMinutes ?? 0}
            onResolved={onSaved}
          />
        )}
        {rec.overtimeApprovalStatus === 'APPROVED' && (rec.overtimeMinutes ?? 0) > 0 && (
          <div className="text-xs text-sage-600 bg-sage-50 rounded px-3 py-2">
            ✓ 加班 {rec.overtimeMinutes} 分鐘已核准
          </div>
        )}
        {rec.overtimeApprovalStatus === 'REJECTED' && (rec.overtimeMinutes ?? 0) > 0 && (
          <div className="text-xs text-rose-500 bg-rose-50 rounded px-3 py-2">
            ✕ 加班 {rec.overtimeMinutes} 分鐘已拒絕（不計入工時）
          </div>
        )}

        <div className="text-xs text-morandi-400 bg-morandi-50 rounded px-3 py-2">
          ⚠ 老闆手動修改後此筆將標記為「已核准」
        </div>

        <div className="flex gap-2 pt-2">
          <button
            onClick={handleDelete}
            disabled={saving}
            className="px-3 py-2.5 bg-white border border-rose-300 text-rose-500 hover:bg-rose-50 rounded-btn text-sm disabled:opacity-50"
          >刪除</button>
          <button
            onClick={onClose}
            disabled={saving}
            className="flex-1 py-2.5 bg-morandi-100 hover:bg-morandi-200 text-morandi-600 rounded-btn text-sm disabled:opacity-50"
          >取消</button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex-1 py-2.5 bg-earth-400 hover:bg-earth-500 text-white rounded-btn text-sm disabled:opacity-50"
          >{saving ? '儲存中…' : '儲存'}</button>
        </div>
      </div>
    </div>
  );
}

// 加班核准區塊（PENDING 狀態用）
function OvertimeApprovalSection({ recId, minutes, onResolved }: {
  recId: string;
  minutes: number;
  onResolved: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [showRejectInput, setShowRejectInput] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  const approve = async () => {
    setBusy(true);
    try {
      await api.put(`/attendance/${recId}/overtime`, { action: 'APPROVED' });
      onResolved();
    } catch (err) {
      alert('核准失敗：' + (err as Error).message);
      setBusy(false);
    }
  };

  const reject = async () => {
    setBusy(true);
    try {
      await api.put(`/attendance/${recId}/overtime`, { action: 'REJECTED', reason: rejectReason || undefined });
      onResolved();
    } catch (err) {
      alert('拒絕失敗：' + (err as Error).message);
      setBusy(false);
    }
  };

  return (
    <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-3 space-y-2">
      <div className="text-sm font-medium text-amber-800">
        ⏳ 加班待核准：{minutes} 分鐘
      </div>
      {!showRejectInput ? (
        <div className="flex gap-2">
          <button
            onClick={() => setShowRejectInput(true)}
            disabled={busy}
            className="flex-1 py-2 text-sm bg-white border border-rose-300 text-rose-500 hover:bg-rose-50 rounded-btn disabled:opacity-50"
          >拒絕</button>
          <button
            onClick={approve}
            disabled={busy}
            className="flex-1 py-2 text-sm bg-sage-500 text-white hover:bg-sage-600 rounded-btn disabled:opacity-50"
          >{busy ? '處理中…' : '✓ 核准加班'}</button>
        </div>
      ) : (
        <div className="space-y-2">
          <input
            type="text"
            value={rejectReason}
            onChange={e => setRejectReason(e.target.value)}
            placeholder="拒絕理由（選填）"
            maxLength={500}
            className="input-field text-sm"
          />
          <div className="flex gap-2">
            <button
              onClick={() => setShowRejectInput(false)}
              disabled={busy}
              className="flex-1 py-2 text-sm bg-morandi-100 text-morandi-600 hover:bg-morandi-200 rounded-btn disabled:opacity-50"
            >取消</button>
            <button
              onClick={reject}
              disabled={busy}
              className="flex-1 py-2 text-sm bg-rose-500 text-white hover:bg-rose-600 rounded-btn disabled:opacity-50"
            >{busy ? '處理中…' : '確認拒絕'}</button>
          </div>
        </div>
      )}
    </div>
  );
}

function MiniStat({ label, value, unit, highlight }: {
  label: string;
  value: number;
  unit: string;
  highlight?: boolean;
}) {
  return (
    <div className={`card-morandi p-3 text-center ${highlight ? 'ring-1 ring-rose-300' : ''}`}>
      <div className={`text-lg font-bold font-mono ${highlight ? 'text-rose-500' : 'text-morandi-700'}`}>{value}</div>
      <div className="text-xs text-morandi-400">{label} <span>{unit}</span></div>
    </div>
  );
}
